import { CostCenter, iLine, Material } from "/app/src/models";

import { useCallback } from "react";
import { DatePicker, Form, Input, InputNumber, Select } from "formik-antd";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import {
  costCenterService,
  iLineService,
  materialService,
} from "/app/src/services";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Box from "/app/src/components/generic/components/box";
import FormikDisabler from "/app/src/components/generic/components/formikDisabler";
import DisabledSubmitButton from "/app/src/components/generic/components/buttons/DisabledSubmitButton";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { handlePromiseError } from "/app/src/helpers/api";
import HeroDivider from "/app/src/components/HeroUi/Divider";
import { useOrderFields } from "/app/src/components/apps/orderBuilder/settings/fields/orderFieldsData";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import { Accordion, AccordionItem } from "@heroui/react";
import { Moment } from "moment";

interface FormValues {
  quantity?: number;
  material?: string;
}

/**
 * Formats the form values to be sent to the backend API
 */
function formatForm(values: FormValues) {
  return { data: JSON.stringify(values) };
}

/**
 * Displays the details of a line in an order created through the Order Builder App
 */
/**
 * Renders a line item with accordion for editing.
 * @param {object} props - Component props.
 * @param {iLine} props.line - The line data.
 * @param {boolean} props.disabled - Whether the line is disabled.
 * @param {number} props.integrationId - Integration ID.
 * @returns {JSX.Element} The Line component.
 */
export default function Line({
  line,
  disabled,
  integrationId,
}: {
  line: iLine;
  disabled: boolean;
  integrationId: number;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { orderLineFields } = useOrderFields(integrationId, "line");

  /**
   * Toggles the indicator icon based on open state.
   * @param {object} param0 - The object containing the open state.
   * @param {boolean} param0.isOpen - Indicates if the accordion is open.
   * @returns {JSX.Element} The indicator icon component element.
   */
  const toggleIndicatorIcon = useCallback(({ isOpen }) => {
    return isOpen ? (
      <IconBuilder icon="ChevronRight" />
    ) : (
      <IconBuilder icon="ChevronDown" />
    );
  }, []);

  /**
   * Renders the accordion title with material and quantity.
   * @returns {JSX.Element} The title content for the accordion.
   */
  const accordionTitle = () => {
    return (
      <div className="flex items-center space-x-8">
        <h2 className="mb-0 text-[18px] w-[300px] truncate">
          {t("translation:material")}: {line.loadedData?.materialName}
        </h2>
        <h2 className="mb-0 text-[18px]">
          {t("translation:quantity")}: {line.loadedData?.quantity}
        </h2>
      </div>
    );
  };

  /**
   * Updates the query data for the specified line.
   * @param {number} lineId - The ID of the line to update.
   * @param {iLine} data - The new line data.
   * @returns {void}
   */
  const updateQueryData = useCallback(
    (lineId: number, data: iLine) => {
      queryClient.setQueryData(
        ["lines", { orderId: data.orderId }],
        (oldData: { lines: iLine[] }) => {
          return {
            lines: oldData.lines.map((l) => {
              if (l.id === lineId) {
                return data;
              }
              return l;
            }),
          };
        },
      );
    },
    [queryClient],
  );

  const { data: materials } = useQuery({
    queryKey: ["materials"],
    queryFn: () => {
      return materialService.getAll();
    },
    initialData: { materials: [] },
    select: (data: { materials: Material[] }) => data.materials,
  });

  /**
   * Handles form submission to update a line.
   * @param {FormValues} values - The form values.
   * @param {FormikHelpers<FormValues>} actions - Formik helper actions.
   * @returns {Promise<void>} A promise that resolves when update completes.
   */
  const handleUpdate = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      await iLineService
        .updateSingle(line.id, formatForm(values))
        .then(handlePromiseError)
        .then((response) => {
          updateQueryData(line.id, response.line);
          queryClient.invalidateQueries(["revisions", String(line.orderId)]);
          actions.resetForm();
        });
    },
    [line.id, line.orderId, queryClient, updateQueryData],
  );

  const { data: costCenters } = useQuery({
    queryKey: ["costCenters"],
    queryFn: () => {
      return costCenterService.getAll();
    },
    initialData: { cost_centers: [] },
    select: (data: { cost_centers: CostCenter[] }) => data.cost_centers,
  });

  /**
   * Creates dynamic form fields for order line.
   * @returns {JSX.Element} The dynamic form fields.
   */
  const createDynamicOrderLineFieldsForm = useCallback(
    (values: FormValues, setFieldValue) => {
      // Dynamically create onChangeDatePicker function for each field
      const getOnChangeDatePicker = (fieldName: string) => (value: Moment) => {
        setFieldValue(fieldName, value);
      };
      return (
        <>
          <Form.Item name="materialName" label={t("translation:material_name")}>
            <Select
              options={materials.map((material) => ({
                label: material.name,
                value: material.name,
              }))}
              name="materialName"
              size="large"
            />
          </Form.Item>
          <Form.Item name="quantity" label={t("translation:quantity")}>
            <InputNumber
              style={{ width: "100%" }} // Ensure the input takes full width
              name="quantity"
              size="large"
            />
          </Form.Item>
          {orderLineFields
            .filter((field) => field.name !== "materialName")
            .map((field) => (
              <Form.Item
                key={field.name}
                hasFeedback={false}
                name={field.name}
                label={field.values}
              >
                {field.name === "expiryDate" ||
                field.name === "productionDate" ||
                field.name === "releaseDate" ? (
                  <DatePicker
                    name={field.name}
                    showTime
                    onChange={getOnChangeDatePicker(field.name)}
                    size="large"
                    style={{ width: "100%" }}
                  />
                ) : field.name === "costCenterId" ? (
                  <Select
                    name={field.name}
                    placeholder={t("translation:cost_center")}
                    size="large"
                  >
                    {costCenters?.map((c) => (
                      <Select.Option key={c.id} value={c.id}>
                        {c.name}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Input name={field.name} size="large" />
                )}
              </Form.Item>
            ))}
        </>
      );
    },
    [costCenters, materials, orderLineFields, t],
  );

  /**
   * Renders form for line editing.
   * @param {FormikProps<FormValues>} props - Formik props including dirty and isValid.
   * @returns {JSX.Element} The form component.
   */
  const lineForm: (props: FormikProps<FormValues>) => JSX.Element = useCallback(
    ({ dirty, isValid, values, setFieldValue }) => {
      return (
        <Form layout="vertical">
          <FormikDisabler disabled={disabled} />
          <div className="flex justify-end">
            <div className="w-full max-w-[100px]">
              <DisabledSubmitButton
                type="primary"
                block
                disabled={!(dirty && isValid)}
                className="w-full"
              >
                {t("translation:save")}
              </DisabledSubmitButton>
            </div>
          </div>
          <HeroDivider className="my-3" />

          <GridWrapper responsive={{ sm: 1, md: 1, lg: 3 }}>
            {createDynamicOrderLineFieldsForm(values, setFieldValue)}
          </GridWrapper>
        </Form>
      );
    },
    [createDynamicOrderLineFieldsForm, disabled, t],
  );

  /**
   * Gets initial form values from line data.
   * @returns {FormValues} The initial form values.
   */
  const getInitValues = useCallback(() => {
    return { ...line.loadedData };
  }, [line]);
  return (
    <Box className="!mb-0">
      <Accordion
        itemClasses={{
          trigger: "py-0",
        }}
        selectionMode="single"
        showDivider
        className="!px-0"
      >
        <AccordionItem
          textValue="line"
          classNames={{
            title: "text-[18px]",
          }}
          key="1"
          title={accordionTitle()}
          indicator={toggleIndicatorIcon}
        >
          <Formik
            enableReinitialize
            validationSchema={simpleSchemaBuilder([
              {
                name: "materialName",
                type: "string",
                required: true,
              },
              {
                name: "quantity",
                type: "number",
                required: true,
              },
              ...orderLineFields
                .filter((field) => field.name !== "materialName")
                .map((field) => ({
                  name: field.name,
                  type: "string" as const,
                  required: field.required,
                })),
            ])}
            component={lineForm}
            initialValues={getInitValues()}
            onSubmit={handleUpdate}
          />
        </AccordionItem>
      </Accordion>
    </Box>
  );
}
